<template>
  <div class="p-3 bg-white rounded border h-100">
    <h4 class="h5">
      <i class="fas fa-copy me-2"></i>
      Copiar Base de Datos
    </h4>
    <p class="text-muted small">
      Crea una copia completa de la base de datos actual en una nueva base de datos con el nombre que especifiques.
    </p>
    <div class="alert alert-warning py-2 small mb-3">
      <strong>⚠️ Importante:</strong> Esta operación copiará todas las colecciones y documentos de la base de datos actual a una nueva base de datos. La operación puede tardar varios minutos dependiendo del tamaño de la base de datos.
    </div>
    <div class="mb-3">
      <label for="targetDbName" class="form-label">Nombre de la Base de Datos Destino</label>
      <input
        type="text"
        id="targetDbName"
        v-model="targetDbName"
        class="form-control"
        :disabled="loading"
        placeholder="Ej: backup_2024"
        pattern="[a-zA-Z0-9_]+"
        title="Solo letras, números y guiones bajos"
      />
      <small class="form-text text-muted">
        El nombre debe contener solo letras, números y guiones bajos. No puede contener espacios ni caracteres especiales.
      </small>
    </div>
    <button
      type="button"
      class="btn btn-primary"
      @click="copyDatabase"
      :disabled="!targetDbName || loading || !isValidDbName"
    >
      <i v-if="loading" class="fas fa-spinner fa-spin me-2"></i>
      <i v-else class="fas fa-copy me-2"></i>
      {{ loading ? "Copiando..." : "Copiar Base de Datos" }}
    </button>
    <div v-if="copyProgress" class="mt-3">
      <div class="progress mb-2">
        <div
          class="progress-bar progress-bar-striped progress-bar-animated"
          role="progressbar"
          :style="{ width: copyProgress.percentage + '%' }"
        >
          {{ copyProgress.percentage }}%
        </div>
      </div>
      <p class="small text-muted mb-0">{{ copyProgress.message }}</p>
    </div>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import Swal from "sweetalert2";

export default {
  name: "DatabaseCopy",
  data() {
    return {
      targetDbName: "",
      loading: false,
      copyProgress: null,
    };
  },
  computed: {
    isValidDbName() {
      // Validar que el nombre solo contenga letras, números y guiones bajos
      return /^[a-zA-Z0-9_]+$/.test(this.targetDbName);
    },
  },
  methods: {
    async copyDatabase() {
      if (!this.targetDbName || !this.isValidDbName) {
        Swal.fire({
          title: "Error",
          text: "Por favor, ingresa un nombre válido para la base de datos destino",
          icon: "error",
        });
        return;
      }

      const result = await Swal.fire({
        title: "¿Estás seguro?",
        html: `
          <p>Se creará una copia completa de la base de datos actual en:</p>
          <p><strong>${this.targetDbName}</strong></p>
          <p class="text-muted small">Esta operación puede tardar varios minutos.</p>
        `,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sí, copiar",
        cancelButtonText: "Cancelar",
      });

      if (!result.isConfirmed) {
        return;
      }

      this.loading = true;
      this.copyProgress = {
        percentage: 0,
        message: "Iniciando copia de base de datos...",
      };

      try {
        const token = localStorage.getItem("token");
        const response = await wencesApi.post(
          "/api/database/copy",
          { targetDbName: this.targetDbName },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            // Aumentar timeout para operaciones largas (10 minutos)
            timeout: 600000,
            // Configurar para recibir actualizaciones de progreso si el backend las envía
            onUploadProgress: (progressEvent) => {
              // Esto no se usará en POST, pero lo dejamos por si acaso
            },
          }
        );

        if (response.data.success) {
          this.copyProgress = {
            percentage: 100,
            message: "Copia completada exitosamente",
          };

          Swal.fire({
            title: "¡Éxito!",
            html: `
              <p>La base de datos ha sido copiada exitosamente a:</p>
              <p><strong>${this.targetDbName}</strong></p>
              <p class="text-muted small mt-2">
                Colecciones copiadas: ${response.data.data?.collectionsCopied || "N/A"}<br>
                Documentos copiados: ${response.data.data?.documentsCopied || "N/A"}
              </p>
            `,
            icon: "success",
            timer: 5000,
            showConfirmButton: true,
          });

          // Limpiar el formulario
          this.targetDbName = "";
          this.copyProgress = null;
        } else {
          throw new Error(response.data.error || "Error en la copia");
        }
      } catch (error) {
        console.error("Error copying database:", error);
        let errorMessage = "Error al copiar la base de datos";
        if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error.message) {
          errorMessage = error.message;
        }
        Swal.fire({
          title: "Error",
          text: errorMessage,
          icon: "error",
        });
        this.copyProgress = null;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.progress {
  height: 25px;
}

.progress-bar {
  font-size: 0.85rem;
  line-height: 25px;
}
</style>

